import type { GeoPoint, IsoDateTime } from './common.ts';
import type { SourceRef } from './source.ts';

export type TransportMode = 'rer' | 'train' | 'metro' | 'tram' | 'bus' | 'coach' | 'other';

export interface TransportLine {
  /** Identifiant interne stable, ex. "rer-c". */
  readonly id: string;
  /** Code affiché, ex. "C", "H". */
  readonly code: string;
  readonly name: string;
  readonly mode: TransportMode;
  /** Couleurs officielles du référentiel (#RRGGBB). */
  readonly color?: string;
  readonly textColor?: string;
}

export interface TransportStop {
  /** Identifiant interne stable, ex. "ermont-eaubonne". */
  readonly id: string;
  readonly name: string;
  readonly coordinates: GeoPoint;
  readonly modes: readonly TransportMode[];
  readonly lineIds: readonly string[];
}

export type DepartureStatus = 'on_time' | 'delayed' | 'cancelled' | 'unknown';

export interface TransportDeparture {
  readonly id: string;
  readonly stopId: string;
  readonly line: TransportLine;
  readonly destination: string;
  readonly platform?: string;
  /** Horaire théorique. */
  readonly scheduledAt?: IsoDateTime;
  /** Horaire estimé (temps réel), ou théorique si `isRealtime` est faux. */
  readonly expectedAt: IsoDateTime;
  readonly isRealtime: boolean;
  readonly status: DepartureStatus;
  readonly source: SourceRef;
}

export type DisruptionSeverity = 'info' | 'minor' | 'major' | 'blocking';

export interface TransportDisruption {
  readonly id: string;
  readonly title: string;
  readonly message: string;
  readonly severity: DisruptionSeverity;
  readonly lineIds: readonly string[];
  readonly stopIds: readonly string[];
  readonly validFrom?: IsoDateTime;
  readonly validUntil?: IsoDateTime;
  readonly source: SourceRef;
}

export type NetworkStatus = 'normal' | 'disrupted' | 'heavily_disrupted' | 'unknown';

/** Minutes entières restantes avant le départ (arrondi inférieur, jamais négatif). */
export function minutesUntil(departure: Pick<TransportDeparture, 'expectedAt'>, now: Date): number {
  const ms = Date.parse(departure.expectedAt) - now.getTime();
  return Math.max(0, Math.floor(ms / 60_000));
}

/**
 * Départs encore à venir, triés. Un départ passé depuis plus de `graceMs` est retiré :
 * une liste servie depuis un cache ne doit jamais proposer un train déjà parti.
 */
export function upcomingDepartures<T extends Pick<TransportDeparture, 'expectedAt' | 'status'>>(
  departures: readonly T[],
  now: Date,
  graceMs = 30_000,
): T[] {
  const threshold = now.getTime() - graceMs;
  return departures
    .filter((d) => Date.parse(d.expectedAt) >= threshold)
    .toSorted((a, b) => Date.parse(a.expectedAt) - Date.parse(b.expectedAt));
}

/** État global du réseau à partir des perturbations en cours. */
export function networkStatusFrom(
  disruptions: readonly Pick<TransportDisruption, 'severity' | 'validFrom' | 'validUntil'>[] | null,
  now: Date,
): NetworkStatus {
  if (disruptions === null) return 'unknown';
  const t = now.getTime();
  const active = disruptions.filter(
    (d) =>
      (d.validFrom === undefined || Date.parse(d.validFrom) <= t) &&
      (d.validUntil === undefined || Date.parse(d.validUntil) > t),
  );
  if (active.some((d) => d.severity === 'blocking' || d.severity === 'major')) {
    return 'heavily_disrupted';
  }
  if (active.some((d) => d.severity === 'minor')) return 'disrupted';
  return 'normal';
}
